import React, { createRef, RefObject, useEffect, useState } from 'react';
import { Table, TableBody, TableContainer } from '@mui/material';
import EnhancedTableHead from './components/enhanced-table-head';
import tableStyles from './table-styles';
import ExpandableRow, { HeadCell } from './components/expandable-row';
import { uniqueId, throttle } from '../../globals/utils';

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

type Order = 'asc' | 'desc';

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (a: { [key in Key]: number | string }, b: { [key in Key]: number | string }) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((element, index) => [element, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((element) => element[0]);
}

type tableProperties = {
  id?: string;
  initialSort?: string;
  rows: any;
  expandableData: object[];
  headCells: HeadCell[];
  selectableRows?: Function;
  tableHeight?: string;
  infiniteScrollCallback?: Function;
  rowAlignment?: 'inherit' | 'left' | 'center' | 'right' | 'justify';
  onClickOfDownload?: Function;
  isDownloadDisabled?: boolean;
  downloadIconOrMessage?: any;
  downloadOutline?: boolean;
  downloadStyle?: any;
};
const tableContainer: RefObject<any> = createRef();

const infiniteScrollFinder = (scrollingWindow: any, infiniteScrollCallback: Function) => {
  if (
    infiniteScrollCallback &&
    scrollingWindow.firstChild.offsetHeight ===
      tableContainer.current.offsetHeight + scrollingWindow.scrollTop
  ) {
    infiniteScrollCallback();
  }
};

const EnhancedTable = (properties: tableProperties) => {
  const { initialSort } = properties;
  const classes = tableStyles();
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<keyof any>(initialSort || 'record');
  const [selected, setSelected] = useState<any>([]);
  const [openRows, setOpenRows] = useState([]);
  const {
    id,
    rows,
    expandableData,
    headCells,
    selectableRows,
    tableHeight,
    infiniteScrollCallback,
    rowAlignment,
    onClickOfDownload,
    isDownloadDisabled,
    downloadIconOrMessage,
    downloadOutline,
    downloadStyle,
  } = properties;

  useEffect(() => {
    setSelected([]);
  }, [rows]);

  useEffect(() => {
    if (
      tableContainer &&
      tableContainer.current &&
      tableContainer.current.firstChild &&
      infiniteScrollCallback
    ) {
      const scrollingWindow = tableContainer.current.firstChild;
      scrollingWindow.addEventListener('scroll', () =>
        throttle(
          () => infiniteScrollFinder(scrollingWindow, infiniteScrollCallback),
          tableContainer,
          200,
        ),
      );
    }
  }, [infiniteScrollCallback]);

  useEffect(() => {
    if (selectableRows !== undefined) selectableRows(selected);
  }, [selected, selectableRows]);

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof any) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = rows.map((row: { id: number }) => row.id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event: React.MouseEvent<unknown>, name: string) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected: string[] = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  };

  const isSelected = (name: string) => selected.includes(name);

  return (
    <div id={id} ref={tableContainer} className={classes.root}>
      <TableContainer style={{ height: tableHeight || 'inherit' }}>
        <Table
          stickyHeader
          className={classes.table}
          aria-labelledby="tableTitle"
          size="medium"
          aria-label="enhanced table"
        >
          <EnhancedTableHead
            expandableDataExists={expandableData.length > 0}
            classes={classes}
            numSelected={selected.length}
            order={order}
            orderBy={orderBy as string}
            onSelectAllClick={handleSelectAllClick}
            onRequestSort={handleRequestSort}
            rowCount={rows.length}
            headCells={headCells}
            selectableRows={selectableRows}
            isDownloadable
          />
          <TableBody>
            {stableSort(rows, getComparator(order, orderBy)).map((row, index) => {
              const isItemSelected = isSelected(row.id as string);
              const labelId = `enhanced-table-checkbox-${index}`;
              return (
                <ExpandableRow
                  key={`${row.id}-${uniqueId()}`}
                  row={row}
                  handleClick={handleClick}
                  isItemSelected={isItemSelected}
                  labelId={labelId}
                  headCells={headCells}
                  expandableData={expandableData}
                  id={row.id as number}
                  selectableRows={selectableRows}
                  openRows={openRows}
                  setOpenRows={setOpenRows}
                  rowAlignment={rowAlignment}
                  isDownloadable
                  onClickOfDownload={onClickOfDownload}
                  isDownloadDisabled={isDownloadDisabled}
                  downloadFace={downloadIconOrMessage}
                  downloadOutline={downloadOutline}
                  downloadStyle={downloadStyle}
                />
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default EnhancedTable;
